import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { classes } from '../outils/classes';
import styles from './Carte.module.css';
import { chargerMapLibre } from './carte/charger-maplibre';

export type FormePoint = 'cercle' | 'carre' | 'triangle';
export type TonPoint = 'bleu' | 'vert' | 'jaune' | 'rouge';

export interface PointCarte {
  id: string;
  longitude: number;
  latitude: number;
  libelle: string;
}

export interface CoucheCarte {
  id: string;
  libelle: string;
  /** Forme doublant la couleur : l'information ne repose jamais sur la couleur seule. */
  forme: FormePoint;
  ton: TonPoint;
  points: PointCarte[];
}

export interface ProprietesCarte {
  titre: string;
  couches: CoucheCarte[];
  centre: [number, number];
  zoom?: number;
  hauteur?: number;
  /**
   * Modèle d'URL de tuiles raster (par exemple un serveur OpenStreetMap interne). Absent par
   * défaut : aucune ressource distante n'est chargée et le fond reste uni.
   */
  tuiles?: string | null;
  attribution?: string;
  onPoint?: (couche: CoucheCarte, point: PointCarte) => void;
}

function Forme({ forme, ton }: { forme: FormePoint; ton: TonPoint }) {
  return <span className={classes(styles.forme, styles[forme], styles[ton])} aria-hidden />;
}

function webglDisponible(): boolean {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2'));
  } catch {
    return false;
  }
}

/** Carte territoriale MapLibre GL : couches de points, légende textuelle, liste accessible. */
export function Carte({ titre, couches, centre, zoom = 12, hauteur = 260, tuiles = null, attribution, onPoint }: ProprietesCarte) {
  const conteneur = useRef<HTMLDivElement>(null);
  const [etat, setEtat] = useState<'chargement' | 'pret' | 'indisponible'>('chargement');
  const rappel = useRef(onPoint);
  rappel.current = onPoint;
  const signature = JSON.stringify({ couches, centre, zoom, tuiles });

  useEffect(() => {
    if (!conteneur.current) return;
    if (!webglDisponible()) {
      setEtat('indisponible');
      return;
    }
    let carte: { remove: () => void } | null = null;
    let annule = false;
    void (async () => {
      try {
        const maplibre = await chargerMapLibre();
        await import('maplibre-gl/dist/maplibre-gl.css');
        if (annule || !conteneur.current) return;
        const fondCouleur = getComputedStyle(document.documentElement).getPropertyValue('--cc-surface-2').trim() || '#ecebe4';
        const sources: Record<string, unknown> = {};
        const layers: unknown[] = [{ id: 'fond', type: 'background', paint: { 'background-color': fondCouleur } }];
        if (tuiles) {
          sources.fond = { type: 'raster', tiles: [tuiles], tileSize: 256, attribution: attribution ?? '' };
          layers.push({ id: 'tuiles', type: 'raster', source: 'fond' });
        }
        const instance = new maplibre.Map({
          zoomLevelsToOverscale: undefined, // Conserver le rendu de MapLibre 5.
          container: conteneur.current,
          style: { version: 8, sources, layers } as never,
          center: centre,
          zoom,
          attributionControl: tuiles ? undefined : false,
        });
        instance.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-right');
        for (const couche of couches) {
          for (const p of couche.points) {
            const bouton = document.createElement('button');
            bouton.type = 'button';
            bouton.className = styles.marqueur ?? '';
            bouton.setAttribute('aria-label', `${couche.libelle} : ${p.libelle}`);
            const forme = document.createElement('span');
            forme.className = classes(styles.forme, styles[couche.forme], styles[couche.ton]);
            bouton.appendChild(forme);
            bouton.addEventListener('click', () => rappel.current?.(couche, p));
            new maplibre.Marker({ element: bouton }).setLngLat([p.longitude, p.latitude]).addTo(instance);
          }
        }
        carte = instance;
        setEtat('pret');
      } catch {
        if (!annule) setEtat('indisponible');
      }
    })();
    return () => {
      annule = true;
      carte?.remove();
    };
    // La signature résume les propriétés qui imposent de reconstruire la carte.
  }, [signature]);

  const total = couches.reduce((n, c) => n + c.points.length, 0);
  return (
    <figure className={styles.carte} style={{ margin: 0 }}>
      <figcaption>
        <h3>{titre}</h3>
      </figcaption>
      <div className={styles.fond} style={{ '--hauteur': `${hauteur}px` } as CSSProperties}>
        <div ref={conteneur} style={{ position: 'absolute', inset: 0 }} aria-hidden={etat !== 'pret'} />
        {etat === 'indisponible' ? (
          <p className={styles.message}>
            Affichage cartographique indisponible sur cet appareil (WebGL absent). Les points sont listés ci-dessous.
          </p>
        ) : null}
      </div>
      <ul className={styles.legende} aria-label="Légende">
        {couches.map((c) => (
          <li key={c.id}>
            <Forme forme={c.forme} ton={c.ton} />
            {c.libelle} ({c.points.length})
          </li>
        ))}
      </ul>
      <details open={etat === 'indisponible'}>
        <summary>Liste des {total} points</summary>
        <ul className={styles.points}>
          {couches.flatMap((c) =>
            c.points.map((p) => (
              <li key={`${c.id}-${p.id}`}>
                {c.libelle} : {p.libelle}
              </li>
            )),
          )}
        </ul>
      </details>
    </figure>
  );
}
